import { core, type Day, type Decimal } from '@map-my-money/engine';
import { locale, region, setMessageRegion, t, type MessageKey, type Region } from '../i18n/index.ts';

export { region };

export type Currency = 'GBP' | 'USD';

let currency: Currency = 'GBP';
export const setCurrency = (next: Currency) => {
  currency = next;
};
export function setRegion(jurisdiction: Region | undefined) {
  const next = jurisdiction ?? 'uk';
  setMessageRegion(next);
  currency = next === 'us' ? 'USD' : 'GBP';
}
export const currencySymbol = () => (currency === 'USD' ? '$' : '£');

export const toNumber = (value: Decimal | number): number => (typeof value === 'number' ? value : core.toDouble(value));

const wholeFormatters = new Map<string, Intl.NumberFormat>();
const formatter = (key: string, options: Intl.NumberFormatOptions) => {
  const tag = locale();
  let f = wholeFormatters.get(`${tag}${key}`);
  if (!f) {
    f = new Intl.NumberFormat(tag, options);
    wholeFormatters.set(`${tag}${key}`, f);
  }
  return f;
};

/** "£127,552" (no pence), "−£380" with a true minus sign. */
export function money(value: Decimal | number, options: { sign?: boolean; pence?: boolean } = {}): string {
  const n = toNumber(value);
  const digits = options.pence ? 2 : 0;
  const body = formatter(`m${currency}${digits}`, {
    style: 'currency', currency, currencyDisplay: 'narrowSymbol', minimumFractionDigits: digits, maximumFractionDigits: digits,
  }).format(Math.abs(n));
  const rounded = Math.round(Math.abs(n) * 10 ** digits);
  if (rounded === 0) return body;
  if (n < 0) return `−${body}`;
  return options.sign ? `+${body}` : body;
}

/** "£416K", "£1.21M", "£16.7K", "£950": three significant figures above £1,000. */
export function compactMoney(value: Decimal | number, options: { sign?: boolean } = {}): string {
  const n = toNumber(value);
  const abs = Math.abs(n);
  const symbol = currencySymbol();
  let body: string;
  if (abs >= 1e9) body = `${symbol}${sig(abs / 1e9)}B`;
  else if (abs >= 1e6) body = `${symbol}${sig(abs / 1e6)}M`;
  else if (abs >= 1e3) body = `${symbol}${sig(abs / 1e3)}K`;
  else body = `${symbol}${Math.round(abs)}`;
  if (Math.round(abs) === 0) return `${symbol}0`;
  if (n < 0) return `−${body}`;
  return options.sign ? `+${body}` : body;
}

function sig(v: number): string {
  if (v >= 100) return String(Math.round(v));
  if (v >= 10) return trimZero((Math.round(v * 10) / 10).toFixed(1));
  return trimZero((Math.round(v * 100) / 100).toFixed(2));
}
const trimZero = (s: string) => s.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');

/** Axis labels: "£1.2M", "£900K", "£0". */
export function axisMoney(value: number): string {
  const abs = Math.abs(value);
  const symbol = currencySymbol();
  const sign = value < 0 ? '−' : '';
  if (abs === 0) return `${symbol}0`;
  if (abs >= 1e6) return `${sign}${symbol}${trimZero((abs / 1e6).toFixed(abs % 1e6 === 0 ? 0 : 1))}M`;
  if (abs >= 1e3) return `${sign}${symbol}${trimZero((abs / 1e3).toFixed(abs % 1e3 === 0 ? 0 : 1))}K`;
  return `${sign}${symbol}${Math.round(abs)}`;
}

export function percent(value: number, options: { sign?: boolean; digits?: number } = {}): string {
  const digits = options.digits ?? 0;
  const body = `${Math.abs(value).toFixed(digits)}%`;
  if (Math.abs(value) < 0.5 * 10 ** -digits) return `0%`;
  if (value < 0) return `−${body}`;
  return options.sign ? `+${body}` : body;
}

// MARK: Dates (engine Days are UTC calendar days)

export const dayToDate = (day: Day): Date => new Date(day * 86_400_000);
export const dateToDay = (date: Date): Day => core.fromYmd(date.getFullYear(), date.getMonth() + 1, date.getDate());

const dateFormatters = new Map<string, Intl.DateTimeFormat>();
function dateFormat(day: Day, options: Intl.DateTimeFormatOptions): string {
  const tag = locale();
  const key = tag + JSON.stringify(options);
  let f = dateFormatters.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(tag, { ...options, timeZone: 'UTC' });
    dateFormatters.set(key, f);
  }
  return f.format(dayToDate(day));
}

const shortMonth = (month: number) => t(`month.short.${month}` as MessageKey);

/** "Sep 2032" */
export const monthYear = (day: Day) => {
  const { year, month } = core.toYmd(day);
  return `${shortMonth(month)} ${year}`;
};
const isUs = () => region() === 'us';
/** "1 Sep", US "Sep 1" */
export const dayMonth = (day: Day) => {
  const { month, day: d } = core.toYmd(day);
  return isUs() ? `${shortMonth(month)} ${d}` : `${d} ${shortMonth(month)}`;
};
/** "14 Sep 2026", US "Sep 14, 2026" */
export const fullDate = (day: Day) => `${dayMonth(day)}${isUs() ? ',' : ''} ${core.toYmd(day).year}`;
/** "Sunday 28 September", US "Sunday, September 28" */
export const longWeekday = (day: Day) => dateFormat(day, { weekday: 'long', day: 'numeric', month: 'long' });
/** "Saturday 11 October 2026", US "Saturday, October 11, 2026" */
export const longDate = (day: Day) => {
  const text = dateFormat(day, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return isUs() ? text : text.replace(/^(\p{L}+),/u, '$1');
};
/** "15 July", US "July 15" */
export const dayMonthLong = (day: Day) => dateFormat(day, { day: 'numeric', month: 'long' });
/** "December", or "January 2027" with the year. */
export const monthName = (day: Day, withYear: boolean) => dateFormat(day, withYear ? { month: 'long', year: 'numeric' } : { month: 'long' });
export const yearOnly = (day: Day) => String(core.toYmd(day).year);

/** What a point on the line describes: now ("Now · 11 Oct 2026") or a month's end ("End of Oct 2026"). */
export const linePointLabel = (day: Day, isNow: boolean) => (isNow ? t('line.now', { date: fullDate(day) }) : t('line.endOf', { month: monthYear(day) }));

/** "15 Jul" this year, "15 Jul 2025" in another. */
export const shortDate = (day: Day, today: Day) => (core.toYmd(day).year === core.toYmd(today).year ? dayMonth(day) : fullDate(day));

/** "6yr 5mo", "9mo", "2yr" */
export function span(months: number): string {
  const m = Math.max(0, Math.round(months));
  const years = Math.floor(m / 12);
  const rest = m % 12;
  if (years === 0) return t('span.months', { n: rest });
  if (rest === 0) return t('span.years', { n: years });
  return t('span.yearsMonths', { y: years, m: rest });
}

/** "3 years", "14 months", "1 year" for headline copy. */
export function longSpan(months: number): string {
  const m = Math.abs(Math.round(months));
  if (m >= 12 && m % 12 === 0) return m === 12 ? t('span.yearLong') : t('span.yearsLong', { n: m / 12 });
  if (m >= 24 && m % 12 >= 10) return t('span.yearsLong', { n: Math.round(m / 12) });
  return m === 1 ? t('span.monthLong') : t('span.monthsLong', { n: m });
}

export function daysSpan(days: number): string {
  if (days >= 14) {
    const weeks = Math.round(days / 7);
    return weeks === 1 ? t('span.week') : t('span.weeks', { n: weeks });
  }
  return days === 1 ? t('span.day') : t('span.days', { n: days });
}

/** Relative wall-clock time for history rows: "Today, 14:02", "Yesterday, 09:10", "14 Sep, 21:40"; US "Today, 2:02 PM", "Sep 14, 9:40 PM". */
export function whenLabel(iso: string, now: Date): string {
  const at = new Date(iso);
  const time = isUs()
    ? new Intl.DateTimeFormat(locale(), { hour: 'numeric', minute: '2-digit' }).format(at).replace(/\u202f/g, ' ')
    : new Intl.DateTimeFormat(locale(), { hour: '2-digit', minute: '2-digit' }).format(at);
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(at)) / 86_400_000);
  if (diffDays === 0) return t('time.today', { time });
  if (diffDays === 1) return t('time.yesterday', { time });
  const date = isUs() ? `${shortMonth(at.getMonth() + 1)} ${at.getDate()}` : `${at.getDate()} ${shortMonth(at.getMonth() + 1)}`;
  return `${date}, ${time}`;
}

export function agoLabel(iso: string, now: Date): string {
  const minutes = Math.max(0, Math.round((now.getTime() - new Date(iso).getTime()) / 60_000));
  if (minutes < 1) return t('common.justNow');
  if (minutes < 60) return t('common.minutesAgo', { n: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('common.hoursAgo', { n: hours });
  return t('common.daysAgo', { n: Math.round(hours / 24) });
}
